import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

/**
 * PWALoginGate
 *
 * When the app is opened as an installed PWA (display-mode: standalone) AND
 * the user has no active session, redirect once to /ingresar. This mirrors a
 * native app's login-first UX. On the web (browser tab) or when a session
 * exists this does nothing.
 *
 * The redirect ONLY happens on the initial mount. Once redirected the user
 * can register or log in normally; subsequent navigations are respected.
 *
 * Allowed public routes that are exempt (the client is trying to reach these
 * on purpose from a share/deep link):
 *   /ingresar, /registro, /terminos, /agendar/*, /firmar/*
 */
const EXEMPT_PATHS = ["/ingresar", "/registro", "/terminos"];

const isStandalonePWA = () => {
  if (typeof window === "undefined") return false;
  // iOS Safari (Safari-specific navigator flag)
  if (window.navigator?.standalone === true) return true;
  // Android + desktop PWA (CSS media query)
  try {
    return window.matchMedia("(display-mode: standalone)").matches;
  } catch {
    return false;
  }
};

const isExemptPath = (path) => {
  if (!path) return false;
  if (EXEMPT_PATHS.includes(path)) return true;
  if (path.startsWith("/agendar/")) return true; // one-time booking links open without session
  if (path.startsWith("/firmar/")) return true;  // one-time signature links open without session
  if (path.startsWith("/servicios")) return true; // public catalog / shareable link
  if (path.startsWith("/catalogo")) return true;  // alias for public catalog
  if (path.startsWith("/testimonios")) return true; // public testimonials page
  if (path.startsWith("/testimonials")) return true;
  return false;
};

export const PWALoginGate = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;                          // wait for auth to resolve
    if (user) return;                             // already logged in
    if (!isStandalonePWA()) return;               // browser tab, not PWA
    if (isExemptPath(location.pathname)) return;  // already on allowed page

    // Preserve the intended destination so we can send them back after login
    // (only for non-trivial paths).
    const dest = location.pathname + (location.search || "");
    const state = dest === "/" ? undefined : { from: dest };
    navigate("/ingresar", { replace: true, state });
  }, [loading, user, location.pathname, location.search, navigate]);

  return null;
};

export default PWALoginGate;
